import { useEffect } from 'react';

/**
 * Shared per-route SEO manager for this client-rendered SPA.
 *
 * React Router swaps pages without a full document reload, so any tags
 * written to <head> by one page must be explicitly restored when the user
 * navigates away — otherwise (e.g. Industrial -> Home) the previous page's
 * <title> and meta description "leak" onto the next route.
 *
 * This component snapshots whatever was in <head> before it runs, applies
 * the new values, and puts the originals back on unmount.
 *
 * Usage:
 *   <Seo
 *     title="..."
 *     description="..."
 *     keywords="..."
 *     canonical="https://www.beta-edge.com/consulting"
 *     ogImage="https://www.beta-edge.com/og-consulting.jpg"
 *     jsonLd={[{ '@context': 'https://schema.org', ... }]}
 *   />
 */
const setMeta = (name, content, isProperty = false) => {
  if (!content) return null;
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  const existed = !!el;
  const previousContent = el ? el.getAttribute('content') : null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return { el, existed, previousContent };
};

const Seo = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage,
  twitterTitle,
  twitterDescription,
  twitterImage,
  jsonLd,
}) => {
  useEffect(() => {
    const previousTitle = document.title;
    if (title) document.title = title;

    const restoreRefs = [
      setMeta('description', description),
      setMeta('keywords', keywords),
      setMeta('og:title', ogTitle || title, true),
      setMeta('og:description', ogDescription || description, true),
      setMeta('og:url', ogUrl, true),
      setMeta('og:image', ogImage, true),
      setMeta('twitter:title', twitterTitle || ogTitle || title),
      setMeta('twitter:description', twitterDescription || ogDescription || description),
      setMeta('twitter:image', twitterImage || ogImage),
    ].filter(Boolean);

    let canonicalEl = null;
    let canonicalExisted = false;
    let canonicalPrevious = null;
    if (canonical) {
      canonicalEl = document.querySelector('link[rel="canonical"]');
      canonicalExisted = !!canonicalEl;
      canonicalPrevious = canonicalEl ? canonicalEl.getAttribute('href') : null;
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute('href', canonical);
    }

    const scripts = [];
    if (jsonLd) {
      const entries = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      entries.forEach((entry, idx) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-injected', 'true');
        script.textContent = JSON.stringify(entry);
        document.head.appendChild(script);
        scripts.push(script);
      });
    }

    return () => {
      document.title = previousTitle;

      restoreRefs.forEach(({ el, existed, previousContent }) => {
        if (existed) {
          el.setAttribute('content', previousContent ?? '');
        } else {
          el.remove();
        }
      });

      if (canonicalEl) {
        if (canonicalExisted) {
          canonicalEl.setAttribute('href', canonicalPrevious ?? '');
        } else {
          canonicalEl.remove();
        }
      }

      scripts.forEach((script) => script.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogUrl, ogImage, twitterTitle, twitterDescription, twitterImage, jsonLd]);

  return null;
};

export default Seo;
